import React from 'react';
import { ReactElement, useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Typography } from '../Typography/Typography';
import Accordion from '../Accordion/Accordion';
import DropdownMenu, { TOption } from '../DropdownMenu/DropdownMenu';
import { useClickOutside } from '../../hooks/useClickOutside';
import { Close } from '../../assets/icons/Close';

import OpenrLogo from '../../assets/images/OpenrLogo.svg';
import OpenrLogoGradient from '../../assets/images/OpenrLogoGradient.svg';

import classnames from 'classnames/bind';
import styles from './Sidebar.module.scss';
import { IIconProps } from '../../assets/icons/types';

const cx = classnames.bind(styles);

export interface ISublink {
  label: string;
  path?: string;
  disabled?: boolean;
}

export interface ISidebarContent {
  title: string;
  path?: string;
  icon: ReactElement<IIconProps>;
  content: ISublink[];
  isSelected?: boolean;
}

export interface IHomeLinks {
  title: string;
  path: string;
  icon: ReactElement<IIconProps>;
}

export interface ISidebar {
  open: boolean;
  appName: string;
  sidebarContent?: ISidebarContent[];
  options?: TOption[];
  selectedOption?: TOption;
  currentPath: string;
  onSidebarClick: () => void;
  onOptionClick?: (id: string) => void;
  homeLinks?: IHomeLinks[];
}

const Sidebar = ({
  open,
  appName,
  sidebarContent,
  options,
  selectedOption,
  currentPath,
  onSidebarClick,
  onOptionClick,
  homeLinks,
}: ISidebar): ReactElement => {
  const [isHover, setIsHover] = useState<boolean>(false);
  const [openAccordions, setOpenAccordions] = useState<boolean[]>(Array(sidebarContent?.length).fill(true));

  const handleAccordionExpand = (index: number) =>
    setOpenAccordions(
      openAccordions.map((value: boolean, i: number) => {
        return index === i ? !value : value;
      }),
    );

  const handleMouseOver = (): void => setIsHover(true);
  const handleMouseOut = (): void => setIsHover(false);

  const ref = useClickOutside<HTMLDivElement>(() => onSidebarClick());

  useEffect(() => {
    sidebarContent &&
      setOpenAccordions(
        sidebarContent.map((sidebarCategory: ISidebarContent) => {
          if (sidebarCategory?.isSelected || sidebarCategory.path === currentPath) return true;

          return sidebarCategory.content.some(
            (subLink: ISublink) => subLink.path && currentPath.includes(subLink.path),
          );
        }),
      );
  }, [currentPath]);

  return (
    <div data-testid="sidebar-container" className={styles.container}>
      <div className={cx('sidebar', { 'sidebar-open': open, 'sidebar-closed': !open })} ref={ref}>
        <div className={cx('logo-container')}>
          <NavLink
            className={cx('openr-link')}
            to="/"
            aria-label="openr-logo"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            <img src={isHover ? OpenrLogo : OpenrLogoGradient} alt={'Openr logo'} />
            <div className={cx('app-name')}>
              <Typography size="S" color="primary" weight="semi-bold" transform="capitalize">
                {appName}
              </Typography>
            </div>
          </NavLink>
          <button className={cx('icon-button')} onClick={onSidebarClick} aria-label="close-sidebar-button">
            <Close className="close-icon" />
          </button>
        </div>

        {homeLinks && (
          <>
            <Typography size="M" color="primary" weight="semi-bold" className={cx('title')}>
              HOME
            </Typography>
            {homeLinks.map(({ title, path, icon }: IHomeLinks) => {
              const clonedIcon = React.cloneElement(icon, { size: 'small', className: styles['icon'] });
              return (
                <Link role="link" to={path} aria-label={`${title}-link`} key={title}>
                  <div className={currentPath === path ? styles['selected'] : styles['header-container']}>
                    <div className={styles['title-container']}>
                      {clonedIcon}
                      <Typography className={styles['header']}>{title}</Typography>
                    </div>
                  </div>
                </Link>
              );
            })}
          </>
        )}

        {sidebarContent && (
          <>
            <Typography size="M" color="primary" weight="semi-bold" className={cx('title')}>
              TOOLS
            </Typography>
            {sidebarContent.map((accordion: ISidebarContent, index: number) => (
              <div key={`accordion-${accordion.title}`}>
                <Accordion
                  title={accordion.title}
                  icon={accordion.icon}
                  index={index}
                  isExpanded={openAccordions[index]}
                  onExpand={handleAccordionExpand}
                  content={accordion.content}
                  currentPath={currentPath}
                  isSelected={accordion?.isSelected}
                />
              </div>
            ))}
          </>
        )}

        {options && selectedOption && onOptionClick && (
          <div className={cx('account-container')}>
            <DropdownMenu options={options} selectedOption={selectedOption} onOptionClick={onOptionClick} />
          </div>
        )}
      </div>
      <div className={cx('overlay', { 'overlay-open': open })}></div>
    </div>
  );
};

export default Sidebar;
